import React from "react";
import { useFormContext } from "react-hook-form";
import Input from "components/Input";

/* ────────────────────────────────────────────────────────────
   DualModeInput — a numeric field that can be entered either as
   a percentage or as a rupee amount, with a small segmented
   toggle to switch between the two.

   WHY THIS EXISTS
   The same component was duplicated inside CommonSalryStructure
   and UpdateSalaryStructure, and in both copies the toggle was
   absolutely positioned over the label row (`absolute right-0
   top-0.5 … z-10`). Long labels such as "Annual Bonus (% of
   Basic)" were therefore covered by the toggle.

   WHAT CHANGED
   • Single reusable component (no duplicate copies).
   • The toggle is passed to Input via `labelAction`, so it sits
     BESIDE the label — the label always stays fully visible.
   • Dark mode support (the old toggle was hard-coded to white).
   • Accessible: real toggle semantics + tooltips, and switching
     mode re-validates the value (limits differ per mode).
──────────────────────────────────────────────────────────── */

export interface DualModeInputProps {
    /** Numeric form field. */
    name: string;
    /** Boolean form field storing whether `name` holds a percentage. */
    flagName: string;
    percentageLabel: string;
    amountLabel: string;
    defaultIsPercentage?: boolean;
    compact?: boolean;
    required?: boolean;
    disabled?: boolean;
}

const MODES: { isPercentage: boolean; symbol: string; hint: string }[] = [
    { isPercentage: true, symbol: "%", hint: "Enter this value as a percentage" },
    { isPercentage: false, symbol: "₹", hint: "Enter this value as a rupee amount" },
];

const DualModeInput = ({
    name,
    flagName,
    percentageLabel,
    amountLabel,
    defaultIsPercentage = false,
    compact = false,
    required = false,
    disabled = false,
}: DualModeInputProps) => {
    const { watch, setValue, trigger } = useFormContext();
    const flagValue = watch(flagName);
    const isPercentage =
        flagValue === undefined || flagValue === null ? defaultIsPercentage : Boolean(flagValue);

    const selectMode = (nextIsPercentage: boolean) => {
        if (nextIsPercentage === isPercentage) return;
        setValue(flagName, nextIsPercentage, { shouldDirty: true });
        // Min/max limits depend on the mode, so refresh the value's validation
        trigger(name);
    };

    const toggle = (
        <div
            role="group"
            aria-label={`${percentageLabel} value mode`}
            className="flex rounded-md overflow-hidden border border-slate-200 dark:border-gray-600 text-[11px] leading-none"
        >
            {MODES.map((mode, index) => {
                const selected = mode.isPercentage === isPercentage;
                // NOTE: vertical padding stays small so the toggle never makes the
                // label row taller than a plain Input's — grid rows stay aligned.
                return (
                    <button
                        key={mode.symbol}
                        type="button"
                        disabled={disabled}
                        title={mode.hint}
                        aria-pressed={selected}
                        onClick={() => selectMode(mode.isPercentage)}
                        className={`px-2 py-[3px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60
              ${index > 0 ? "border-l border-slate-200 dark:border-gray-600" : ""}
              ${selected
                                ? "bg-blue-600 text-white"
                                : "bg-white dark:bg-gray-700 text-slate-500 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-600"
                            }`}
                    >
                        {mode.symbol}
                    </button>
                );
            })}
        </div>
    );

    return (
        <Input
            compact={compact}
            name={name}
            label={isPercentage ? percentageLabel : amountLabel}
            type="number"
            required={required}
            disabled={disabled}
            labelAction={toggle}
        />
    );
};

export default DualModeInput;
